import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Save, Loader2, Image as ImageIcon } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import AdminShell from "../components/AdminShell";
import SpeakersManager from "../components/SpeakersManager";
import SponsorsManager from "../components/SponsorsManager";
import ImageUploader from "../components/ImageUploader";
import { api } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

const EMPTY_AGENDA_ROW = { time: "", title: "", description: "" };

export default function AdminEventEditor() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const navigate = useNavigate();
  // Auth is handled globally by nailApi's axios interceptor (JWT from
  // localStorage["nailViet.adminJWT"] and legacy X-Admin-Token from sessionStorage).
  // We just need to redirect back to the list if neither is present.
  const hasAuth = !!(typeof window !== "undefined" && (localStorage.getItem("nailViet.adminJWT") || sessionStorage.getItem(TOKEN_KEY)));

  const [ev, setEv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!hasAuth) { navigate("/admin/login"); return; }
    api.get(`/admin/nail-events/${slug}`).then((r) => setEv({
      ...r.data,
      agenda: r.data.agenda || [],
      gallery: r.data.gallery || [],
      sponsors: r.data.sponsors || [],
      speakers: r.data.speakers || [],
      social_links: r.data.social_links || {},
    })).catch(() => navigate("/admin/nail-events"));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ev) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-espresso-muted" />
      </div>
    );
  }

  const set = (k, v) => setEv((e) => ({ ...e, [k]: v }));
  const updArr = (k, i, kk, v) => setEv((e) => {
    const arr = [...(e[k] || [])];
    arr[i] = { ...arr[i], [kk]: v };
    return { ...e, [k]: arr };
  });
  const removeArr = (k, i) => setEv((e) => ({ ...e, [k]: (e[k] || []).filter((_, idx) => idx !== i) }));
  const addArr = (k, blank) => setEv((e) => ({ ...e, [k]: [...(e[k] || []), { ...blank }] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: ev.name, subtitle: ev.subtitle, description: ev.description,
        event_date: ev.event_date, event_time: ev.event_time, end_time: ev.end_time,
        starts_at: ev.starts_at || null,
        location: ev.location, venue_name: ev.venue_name, address: ev.address,
        city: ev.city, maps_url: ev.maps_url,
        hero_image_url: ev.hero_image_url, banner_image_url: ev.banner_image_url,
        gallery: ev.gallery,
        primary_color: ev.primary_color, secondary_color: ev.secondary_color,
        capacity: ev.capacity ? parseInt(ev.capacity, 10) : null,
        waitlist_enabled: !!ev.waitlist_enabled,
        registration_open: !!ev.registration_open,
        enable_sms: !!ev.enable_sms, enable_email: !!ev.enable_email,
        enable_ai_chat: !!ev.enable_ai_chat, enable_ai_voice: !!ev.enable_ai_voice,
        enable_countdown: !!ev.enable_countdown,
        agenda: ev.agenda, footer_text: ev.footer_text,
        social_links: ev.social_links,
        active: !!ev.active,
        // Phase 5.2 — kiosk fields
        event_kiosk_code: ev.event_kiosk_code || null,
        kiosk_enabled: ev.kiosk_enabled !== false,
        checkin_pin: ev.checkin_pin || null,
        ticket_footer_text: ev.ticket_footer_text || null,
        // Speakers + Sponsors are now managed by their own components/endpoints
        // (event-speakers / event-sponsors junction) — no need to send them here.
      };
      await api.put(`/admin/nail-events/${slug}`, payload);
      setToast(lang === "en" ? "Saved" : "Đã lưu");
      setTimeout(() => setToast(""), 1500);
    } catch (err) {
      setToast(err?.response?.data?.detail || "Error");
      setTimeout(() => setToast(""), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      testid="admin-event-editor"
      subtitle={lang === "en" ? "Event Editor" : "Trình Chỉnh Sửa Sự Kiện"}
      title={ev.name}
      backTo="/admin/nail-events"
      actions={
        <button type="button" onClick={save} disabled={saving} data-testid="editor-save"
          className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-5 py-2 text-sm font-medium hover:bg-espresso-hover disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {lang === "en" ? "Save" : "Lưu"}
        </button>
      }
    >
      <div className="max-w-[1100px] space-y-8">
        {toast && <div className="inline-block bg-[#E1EDE6] text-[#2D4A3E] rounded-xl px-4 py-2 text-sm font-medium" data-testid="editor-toast">{toast}</div>}

        <Section title={lang === "en" ? "Basics" : "Thông tin cơ bản"}>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name" value={ev.name} onChange={(v) => set("name", v)} testId="ev-name" />
            <Field label="Subtitle" value={ev.subtitle} onChange={(v) => set("subtitle", v)} testId="ev-subtitle" />
            <Textarea label="Description" value={ev.description} onChange={(v) => set("description", v)} rows={4} testId="ev-description" />
            <div className="md:row-span-2">
              <Textarea label="Footer text" value={ev.footer_text} onChange={(v) => set("footer_text", v)} rows={3} testId="ev-footer" />
            </div>
            <Field label="Event date" value={ev.event_date} onChange={(v) => set("event_date", v)} placeholder="2026-07-18" testId="ev-date" />
            <Field label="Event time" value={ev.event_time} onChange={(v) => set("event_time", v)} placeholder="6:00 PM" testId="ev-time" />
            <Field label="End time" value={ev.end_time} onChange={(v) => set("end_time", v)} placeholder="11:00 PM" testId="ev-end-time" />
            <Field label="Starts at (ISO, for countdown)" value={ev.starts_at || ""} onChange={(v) => set("starts_at", v)} placeholder="2026-07-18T18:00:00-05:00" testId="ev-starts-at" />
          </div>
        </Section>

        <Section title={lang === "en" ? "Venue" : "Địa điểm"}>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Venue name" value={ev.venue_name} onChange={(v) => set("venue_name", v)} testId="ev-venue-name" />
            <Field label="Display location" value={ev.location} onChange={(v) => set("location", v)} testId="ev-location" />
            <Field label="Address" value={ev.address} onChange={(v) => set("address", v)} testId="ev-address" />
            <Field label="City" value={ev.city} onChange={(v) => set("city", v)} testId="ev-city" />
            <Field label="Google Maps URL" value={ev.maps_url} onChange={(v) => set("maps_url", v)} testId="ev-maps" />
          </div>
        </Section>

        <Section title={lang === "en" ? "Media & Theme" : "Hình ảnh & Màu sắc"}>
          <div className="grid md:grid-cols-2 gap-4">
            <ImageUploader
              label={lang === "en" ? "Hero image" : "Ảnh hero"}
              value={ev.hero_image_url}
              onChange={(url) => set("hero_image_url", url)}
              subfolder="events"
              aspect="landscape"
              testId="ev-hero"
              hint={lang === "en" ? "Landscape (16:9), max 10MB." : "Ảnh ngang (16:9), tối đa 10MB."}
            />
            <ImageUploader
              label={lang === "en" ? "Banner image" : "Ảnh banner"}
              value={ev.banner_image_url}
              onChange={(url) => set("banner_image_url", url)}
              subfolder="events"
              aspect="landscape"
              testId="ev-banner"
              hint={lang === "en" ? "Wide banner, max 10MB." : "Banner ngang, tối đa 10MB."}
            />
            <Field label="Primary color (hex)" value={ev.primary_color} onChange={(v) => set("primary_color", v)} placeholder="#2C241B" testId="ev-color-1" />
            <Field label="Secondary color (hex)" value={ev.secondary_color} onChange={(v) => set("secondary_color", v)} placeholder="#C28C7E" testId="ev-color-2" />
          </div>
          <div className="mt-4">
            <GalleryUploader
              label={lang === "en" ? "Gallery images" : "Thư viện ảnh"}
              items={ev.gallery || []}
              onChange={(arr) => set("gallery", arr)}
              testId="ev-gallery"
              lang={lang}
            />
          </div>
        </Section>

        <Section title={lang === "en" ? "Capacity & Toggles" : "Sức chứa & Tùy chọn"}>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Capacity" type="number" value={ev.capacity || ""} onChange={(v) => set("capacity", v)} testId="ev-capacity" />
            <Toggle label={lang === "en" ? "Registration open" : "Mở đăng ký"} value={!!ev.registration_open} onChange={(v) => set("registration_open", v)} testId="ev-reg-open" />
            <Toggle label={lang === "en" ? "Waitlist" : "Danh sách chờ"} value={!!ev.waitlist_enabled} onChange={(v) => set("waitlist_enabled", v)} testId="ev-waitlist" />
            <Toggle label={lang === "en" ? "Countdown" : "Đếm ngược"} value={!!ev.enable_countdown} onChange={(v) => set("enable_countdown", v)} testId="ev-countdown" />
            <Toggle label="SMS" value={!!ev.enable_sms} onChange={(v) => set("enable_sms", v)} testId="ev-sms" />
            <Toggle label="Email" value={!!ev.enable_email} onChange={(v) => set("enable_email", v)} testId="ev-email" />
            <Toggle label="AI Chat" value={!!ev.enable_ai_chat} onChange={(v) => set("enable_ai_chat", v)} testId="ev-ai-chat" />
            <Toggle label="AI Voice" value={!!ev.enable_ai_voice} onChange={(v) => set("enable_ai_voice", v)} testId="ev-ai-voice" />
            <Toggle label={lang === "en" ? "Active" : "Đang chạy"} value={!!ev.active} onChange={(v) => set("active", v)} testId="ev-active" />
          </div>
        </Section>

        <Section title={lang === "en" ? "Kiosk Check-In" : "Kiosk Check-In"}>
          <div className="grid md:grid-cols-3 gap-4">
            <Field
              label={lang === "en" ? "Event kiosk code" : "Mã sự kiện kiosk"}
              value={ev.event_kiosk_code || ""}
              onChange={(v) => set("event_kiosk_code", (v || "").toUpperCase())}
              placeholder="ELK2026"
              testId="ev-kiosk-code"
            />
            <Field
              label={lang === "en" ? "Staff check-in PIN" : "Mã PIN nhân viên"}
              value={ev.checkin_pin || ""}
              onChange={(v) => set("checkin_pin", v)}
              placeholder={lang === "en" ? "Falls back to global PIN" : "Mặc định lấy PIN chung"}
              testId="ev-checkin-pin"
            />
            <Toggle
              label={lang === "en" ? "Kiosk enabled" : "Bật kiosk"}
              value={ev.kiosk_enabled !== false}
              onChange={(v) => set("kiosk_enabled", v)}
              testId="ev-kiosk-enabled"
            />
          </div>
          <div className="mt-4">
            <Field
              label={lang === "en" ? "Ticket footer text (optional)" : "Chữ chân vé (tuỳ chọn)"}
              value={ev.ticket_footer_text || ""}
              onChange={(v) => set("ticket_footer_text", v)}
              placeholder={lang === "en" ? "e.g. See you next year!" : "VD: Hẹn gặp lại năm sau!"}
              testId="ev-ticket-footer"
            />
          </div>
          <p className="mt-3 text-xs text-espresso-muted">
            {lang === "en"
              ? "Staff use the kiosk code + PIN to log a check-in kiosk into this event. PIN falls back to the global CHECKIN_PIN if left blank."
              : "Nhân viên dùng mã sự kiện + PIN để mở kiosk check-in cho sự kiện này. Nếu để trống, hệ thống dùng PIN chung."}
          </p>
        </Section>

        <Section title={lang === "en" ? "Agenda" : "Chương trình"}>
          <div className="space-y-3">
            {(ev.agenda || []).map((row, i) => (
              <div key={i} className="grid md:grid-cols-12 gap-3 items-start bg-linen-100 rounded-2xl p-3" data-testid={`agenda-row-${i}`}>
                <input value={row.time || ""} onChange={(e) => updArr("agenda", i, "time", e.target.value)} placeholder="6:00 PM" className="md:col-span-2 bg-white border border-espresso/10 rounded-xl px-3 py-2 text-sm" />
                <input value={row.title || ""} onChange={(e) => updArr("agenda", i, "title", e.target.value)} placeholder="Title" className="md:col-span-3 bg-white border border-espresso/10 rounded-xl px-3 py-2 text-sm" />
                <input value={row.description || ""} onChange={(e) => updArr("agenda", i, "description", e.target.value)} placeholder="Description" className="md:col-span-6 bg-white border border-espresso/10 rounded-xl px-3 py-2 text-sm" />
                <button type="button" onClick={() => removeArr("agenda", i)} className="md:col-span-1 text-espresso-muted hover:text-[#7A2E2E] justify-self-end">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => addArr("agenda", EMPTY_AGENDA_ROW)} data-testid="agenda-add" className="inline-flex items-center gap-1 text-sm font-medium text-espresso hover:text-espresso-hover">
              <Plus className="w-4 h-4" /> {lang === "en" ? "Add row" : "Thêm dòng"}
            </button>
          </div>
        </Section>

        <Section title={lang === "en" ? "Sponsors" : "Nhà tài trợ"}>
          <SponsorsManager slug={slug} />
        </Section>

        <Section title={lang === "en" ? "Speakers" : "Diễn giả"}>
          <SpeakersManager slug={slug} />
        </Section>
      </div>
    </AdminShell>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-espresso/10 rounded-3xl p-6">
      <h2 className="font-serif text-2xl text-espresso mb-5">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-1.5">{label}</span>
      <input data-testid={testId} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-white border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso" />
    </label>
  );
}

function Textarea({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-1.5">{label}</span>
      <textarea data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="w-full bg-white border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso" />
    </label>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center justify-between bg-linen-100 border border-espresso/10 rounded-xl px-3.5 py-2.5">
      <span className="text-sm font-medium">{label}</span>
      <input data-testid={testId} type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="rounded" />
    </label>
  );
}

function GalleryUploader({ label, items, onChange, testId, lang }) {
  const [pendingIdx, setPendingIdx] = useState(null);
  const t = (vi, en) => (lang === "en" ? en : vi);
  return (
    <div>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-2">{label}</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {items.map((url, i) => (
          <div key={`${i}-${url}`} className="relative group rounded-2xl overflow-hidden border border-espresso/10 bg-linen-100" data-testid={`${testId}-item-${i}`}>
            {url ? (
              <img src={url} alt="" className="w-full aspect-square object-cover" />
            ) : (
              <div className="w-full aspect-square flex items-center justify-center text-espresso-muted">
                <ImageIcon className="w-6 h-6" />
              </div>
            )}
            <button
              type="button"
              className="absolute top-1.5 right-1.5 bg-white/95 rounded-full p-1 shadow hover:bg-white text-espresso"
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              data-testid={`${testId}-remove-${i}`}
              aria-label="remove"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <div className="rounded-2xl border-2 border-dashed border-espresso/25 bg-linen-50/60 min-h-[9rem] flex items-center justify-center">
          {pendingIdx === "add" ? (
            <div className="w-full p-2">
              <ImageUploader
                value=""
                onChange={(url) => { if (url) onChange([...items, url]); setPendingIdx(null); }}
                subfolder="events/gallery"
                aspect="square"
                testId={`${testId}-uploader`}
              />
              <button type="button" onClick={() => setPendingIdx(null)} className="mt-2 text-[11px] text-espresso-muted hover:text-espresso block mx-auto">
                {t("Hủy", "Cancel")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPendingIdx("add")}
              data-testid={`${testId}-add`}
              className="text-xs uppercase tracking-[0.18em] font-semibold text-espresso hover:text-espresso-hover inline-flex flex-col items-center gap-1"
            >
              <Plus className="w-5 h-5" />
              {t("Thêm ảnh", "Add image")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
